<template>
  <div class="register-tab">
    <SalesRegisterToolbar
        ref="toolbarRef"
        :filters="appliedFilters"
        :chip-meta="filterChipMeta"
        :export-options="exportOptions"
        @go="onClickGo"
        @open-filter="onOpenFilter"
        @apply="onApplyFilters"
        @clear="onClearFilters"
        @remove-chip="onRemoveChip"
        @print="onClickToPrint"
        @export="onClickToExport"
    >
      <div class="col-12 col-sm-6">
        <div class="d-flex align-items-center justify-content-between">
          <label class="filter-label">Supplier</label>
          <span class="filter-search-icon" @click="onOpenContactSearchModal">
            <i class="fas fa-search"></i>
          </span>
        </div>
        <AsyncSelect
            placeholder="Supplier Name, ID, Mobile No"
            v-model="draftFilters.supplier"
            :api-service="fetchContactProfiles"
            :additional-query="supplierQueryObj"
            :format-label="formatPatientLabel"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Bill Number</label>
        <input
            type="text"
            class="form-control"
            placeholder="Bill Number"
            :value="billNumberText"
            @input="onInputBillNumber"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Business Unit</label>
        <v-select
            placeholder="Select Business Unit"
            v-model="draftFilters.business_unit"
            :options="businessList"
            label="name"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Project</label>
        <v-select
            placeholder="Select Project"
            v-model="draftFilters.project"
            :options="projectList"
            label="name"
        />
      </div>
    </SalesRegisterToolbar>
    <RegisterTable
        tab="bill"
        :rows="rows"
        :current-page="pageData.current_page"
        :last-page="pageData.last_page"
        :per-page="pageData.per_page"
        :total-value="registerData.total"
        :is-loaded="hasLoaded"
    />
    <div class="mt-2">
      <Pagination :offset="offset" @onPageChange="onPageChange"/>
    </div>
    <ContactDropdownFilter
        v-if="$store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        type="supplier"
    />
    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Pagination from '@/components/atom/Pagination'
import Loader from '@/components/atom/LoaderComponent.vue'
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import SalesRegisterToolbar from '@/components/molecule/company/inventory/product-sales-register/SalesRegisterToolbar.vue'
import RegisterTable from '@/components/molecule/company/inventory/purchase-register/RegisterTable.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import handleContact from '@/services/modules/contact'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'
import usePurchaseRegisterTab from '@/composables/usePurchaseRegisterTab'

defineProps({
  businessList: {
    type: Array,
    default: () => []
  },
  projectList: {
    type: Array,
    default: () => []
  }
})

const { fetchContactProfiles } = handleContact()
const { formatPatientLabel } = useAsyncDropdownHelper()

const supplierQueryObj = { roles: 'supplier' }

const exportOptions = [
  { key: 'bill', label: 'Bill' },
  { key: 'input_tax', label: 'Input Tax Statement' }
]

const filterChipMeta = [
  { key: 'supplier', label: 'Supplier', text_key: 'name' },
  { key: 'bill_number', label: 'Bill Number', text_key: 'text' },
  { key: 'business_unit', label: 'Business Unit', text_key: 'name' },
  { key: 'project', label: 'Project', text_key: 'name' }
]

const getEmptyFilters = () => {
  return {
    supplier: null,
    bill_number: null,
    business_unit: null,
    project: null
  }
}

const getFilterQuery = (filters) => {
  let query = ''
  if (filters.supplier) query += '&contact_profile_id=' + filters.supplier.id
  if (filters.bill_number) query += '&bill_number=' + encodeURIComponent(filters.bill_number.text.trim())
  if (filters.business_unit) query += '&business_id=' + filters.business_unit.id
  if (filters.project) query += '&project_id=' + filters.project.id
  return query
}

const {
  offset,
  loading,
  hasLoaded,
  rows,
  registerData,
  pageData,
  toolbarRef,
  appliedFilters,
  draftFilters,
  onClickGo,
  onPageChange,
  onOpenFilter,
  onApplyFilters,
  onClearFilters,
  onRemoveChip,
  onOpenContactSearchModal,
  onSearchContact,
  onClickToPrint,
  exportTable,
  exportInputTaxStatement
} = usePurchaseRegisterTab('bill', getEmptyFilters, getFilterQuery)

const exportHandlers = {
  bill: exportTable,
  input_tax: exportInputTaxStatement
}

const onClickToExport = (type) => {
  if (! exportHandlers[type]) return
  exportHandlers[type]()
}

const billNumberText = computed(() => {
  if (! draftFilters.value.bill_number) return ''
  return draftFilters.value.bill_number.text
})

const onInputBillNumber = (event) => {
  const text = event.target.value
  draftFilters.value.bill_number = null
  if (! text.trim()) return
  draftFilters.value.bill_number = { text: text }
}
</script>
